import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Icon, Button, Spinner, Can } from '@iq/react-components';

import VersionListItem from '../VersionListItem';
import Delete from '../../../../Delete';
import JSONEditor from '../../../../JSONEditor';
import TypeLabel from '../../../../TypeLabel';
import StyledItem from '../../../../StyledItem';
import ModelEditModal from '../ModelEditModal';
import {
  useFloating,
  useHover,
  useInteractions,
  offset,
  autoUpdate,
  flip,
  shift,
} from '@floating-ui/react';

import { versionSchema } from '../../schemas/versionSchema';
import {
  COMPLETED,
  getModelIdsWithProcessingVersions,
  getUploadingIds,
} from '../../../../../bundles/models';
import { getModelThumbnail, updateSite } from '../../../../../bundles/sites';

const ModelListItem = ({
  model,
  site,
  schema,
  onDelete,
  onSubmit,
  uploads,
  withMappingButtons,
  // onAddVersion,
}) => {
  const dispatch = useDispatch();
  const [isOpen, setOpen] = useState(false);
  const [displayModelEditModal, setDisplayModelEditModal] = useState(false);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const version = model.versions?.at(0);
  const siteModelLinked = site.model === model.id && site.version === version?.id;
  const [isUpdating, setIsUpdating] = useState(false);

  const processingIds = useSelector(getModelIdsWithProcessingVersions);
  const uploadingIds = useSelector(getUploadingIds);

  const updateFields = {
    name: model.name,
    modelType: model.modelType,
  };

  const latestVersion = useMemo(
    () =>
      model.versions
        .filter((v) => v.status === 'COMPLETED' && !v.removed)
        .reduce((acc, v) => {
          if (acc.versionNumber > v.versionNumber) return acc;
          return v;
        }, {}),
    [JSON.stringify(model.versions)]
  );

  const hasPendingVersions = processingIds.includes(model.id);
  const isUploading = uploadingIds.includes(model.id);
  const disabled = hasPendingVersions || isUploading || displayModelEditModal;

  const forgeThumbnail = useSelector((state) => getModelThumbnail(state, model.id, version?.id));

  const hasCustomThumbnail = version?.thumbnail?.split(':')[1]?.split('/')[0] === 'image';
  const hasForgeThumbnail = forgeThumbnail?.split(':')[1]?.split('/')[0] === 'image';
  const displayThumbnail =
    (hasCustomThumbnail && version?.thumbnail) || (hasForgeThumbnail && forgeThumbnail);

  const updateSiteModel = () => {
    setIsUpdating(true);
    dispatch(
      updateSite(
        site.id,
        {
          ...site,
          model: model.id,
          version: version?.id,
        },
        () => setIsUpdating(false)
      )
    );
  };

  const displayModeSecondaryButton =
    version && !version.removed && version.status === COMPLETED ? (
      <Button
        onClick={updateSiteModel}
        disabled={isUpdating || siteModelLinked}
        slim
      >
        {siteModelLinked ? 'Linked to Site Image' : 'Link to Site Image'}
      </Button>
    ) : null;

  const { x, y, strategy, refs, context } = useFloating({
    open: isTooltipVisible,
    placement: 'bottom-end',
    middleware: [offset({ mainAxis: 10, crossAxis: -75 }), flip(), shift()],
    onOpenChange: setIsTooltipVisible,
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);
  return (
    <StyledItem
      itemClass="model-list-item"
      active={isOpen}
      onClick={() => setOpen(!isOpen)}
      headerLabel={
        <TypeLabel
          color={hasPendingVersions ? 'yellow' : 'grey'}
          text={model.modelType}
        />
      }
      headerContent={<div className="title">{model.name}</div>}
      headerActions={
        <>
          {(hasPendingVersions || isUploading) && (
            <Spinner
              size="s"
              className="spinner"
            />
          )}
          {withMappingButtons && (
            <Button
              activity="primary"
              slim
              disabled={disabled}
              onClick={() => setDisplayModelEditModal(true)}
              title={disabled ? 'Available once upload & processing is completed' : ''}
            >
              Edit mapping
            </Button>
          )}
          <Icon icon={isOpen ? 'he-up' : 'he-down'} />
        </>
      }
    >
      <div className="model-thumbnail">
        <div className="model-properties-buttons">
          <JSONEditor
            title="Model properties"
            formData={updateFields}
            schema={schema}
            onFormSubmit={({ formData }) => onSubmit(model.id, { ...model, ...formData })}
            saveButtonText={'Update model'}
            displayModeSecondaryButton={displayModeSecondaryButton}
          />
          <div className="thumbnail-component">
            <div className="thumbnail-tooltip">
              <div
                className="thumbnail-label"
                style={{ marginRight: 'unset' }}
              >
                Thumbnail
              </div>
              <div
                className="tooltip-container"
                style={{ width: 'fit-content', marginBottom: 'unset' }}
                ref={refs.setReference}
                {...getReferenceProps()}
              >
                <Icon
                  icon="he-info"
                  size="s"
                  className="input-tooltip-description"
                />
                {isTooltipVisible && !isUpdating && (
                  <div
                    className="tooltip"
                    ref={refs.setFloating}
                    style={{
                      position: strategy,
                      top: y ?? 0,
                      left: x ?? 0,
                      maxWidth: '100%',
                      pointerEvents: 'none',
                    }}
                    {...getFloatingProps()}
                  >
                    {siteModelLinked
                      ? 'This thumbnail is linked to site image. Updates to the thumbnail also update the site image.'
                      : 'This thumbnail can be linked to site image.'}
                  </div>
                )}
              </div>
            </div>
            <div className="model-version-thumbnail">
              {hasCustomThumbnail || hasForgeThumbnail ? (
                <img
                  src={displayThumbnail}
                  alt={`Thumbnail for ${model.name} - v${latestVersion.versionNumber}`}
                  className="model-page-thumbnail"
                />
              ) : (
                <p className="missing-thumbnail-text">Edit model to add thumbnail</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {model.versions.length > 0 ? (
        <VersionListItem
          key={version}
          version={version}
          model={model}
          schema={versionSchema}
          upload={uploads.find((u) => u.versionId === version.id)}
        />
      ) : (
        ''
      )}

      <Can
        permission="models/Delete"
        scope={{ org: site.org, site: site.id }}
      >
        <Delete
          onDelete={() => onDelete(model.id)}
          title="Delete model"
        />
      </Can>

      {displayModelEditModal && (
        <ModelEditModal
          geometryMapping
          sceneConfig
          title={`${model.name} - v${latestVersion.versionNumber}`}
          model={model}
          version={latestVersion}
          onCloseModal={() => setDisplayModelEditModal(false)}
          activeExtension="mapping"
        />
      )}
    </StyledItem>
  );
};

export default ModelListItem;
